import moment from 'moment'
import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, mergeMap, takeUntil } from 'rxjs/operators'

import { CAMPAIGN_TYPES_VALUES } from 'containers/AwarenessCenter/constants'

import {
  approveCampaignError,
  approveCampaignSuccess,
  getCampaignError,
  getCampaignSuccess,
  removeDraftError,
  removeDraftSuccess,
  saveAndApproveCampaignError,
  saveAndApproveCampaignSuccess,
  saveDraftError,
  saveDraftSuccess,
} from '../steps/actions'
import {
  GET_CAMPAIGN_ASYNC,
  INIT_EDIT_MODE,
  REMOVE_DRAFT_ASYNC,
  SAVE_AND_APPROVE_CAMPAIGN_ASYNC,
  SAVE_DRAFT_ASYNC,
} from '../steps/constants'
import landingPageStepEpics from '../steps/LandingPageStep/epics'
import { validateStepFields as validateSetupStepFields } from '../steps/SetupStep/actions'
import { AUTOMATED_LEVEL_BASED_TRAINING } from '../steps/TrainingModuleStep/constants'

import { CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA } from './constants'

const mapSaveSuccessCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftSuccess,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignSuccess,
  [CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA]: saveDraftSuccess,
}
const mapSaveErrorCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftError,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignError,
  [CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA]: saveDraftError,
}

const getCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(INIT_EDIT_MODE, GET_CAMPAIGN_ASYNC.INITIATE),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSpearPhishingCampaign) return true
      return false
    }),
    mergeMap((action) =>
      api.wizardCampaign
        .getCampaign(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.awarenessCenter.wizardSpearPhishingCampaign.self.campaign.id ||
            action.payload.id
        )
        .pipe(
          mergeMap((response) => of(getCampaignSuccess(response), validateSetupStepFields())),
          catchError((error) => of(epicsErrorHandler(getCampaignError, error))),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_ASYNC.CANCEL)))
        )
    )
  )

const removeDraftEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REMOVE_DRAFT_ASYNC.INITIATE),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSpearPhishingCampaign) return true
      return false
    }),
    mergeMap(() => {
      const id = state$.value.awarenessCenter.wizardSpearPhishingCampaign.self.campaign.id
      const createdSpearPhishingCampaignId =
        state$.value.awarenessCenter.wizardSpearPhishingCampaign.generateEmailsStep
          .createdSpearPhishingCampaignId

      return api.awarenessCenter.deleteCampaign(id || createdSpearPhishingCampaignId).pipe(
        mergeMap(({ response }) => of(removeDraftSuccess(response))),
        catchError((error) => of(epicsErrorHandler(removeDraftError, error))),
        takeUntil(action$.pipe(ofType(REMOVE_DRAFT_ASYNC.CANCEL)))
      )
    })
  )

const saveDraftEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      SAVE_DRAFT_ASYNC.INITIATE,
      SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE,
      CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA
    ),
    filter(() => !!state$.value.awarenessCenter?.wizardSpearPhishingCampaign),
    mergeMap((action) => {
      const campaignData = state$.value.awarenessCenter.wizardSpearPhishingCampaign.self.campaign
      const trainingIds = campaignData.selectedTrainingVideo.reduce((acc, { id }) => {
        if (id !== AUTOMATED_LEVEL_BASED_TRAINING.id) {
          acc.push(id)
        }

        return acc
      }, [])

      if (action.type === CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA) {
        if (
          // notifyMe case
          campaignData.spearPhishingCampaignData.generateScenarios &&
          action.payload.generateScenarios &&
          action.payload.notifyGenerationFinished
        ) {
          campaignData.spearPhishingCampaignData = {
            notifyGenerationFinished: action.payload.notifyGenerationFinished,
          }
        } else {
          // start/cancel generation case
          campaignData.spearPhishingCampaignData = {
            generateScenarios: action.payload.generateScenarios,
            notifyGenerationFinished: action.payload.notifyGenerationFinished,
          }
        }
      } else {
        campaignData.spearPhishingCampaignData = {}
      }

      const createdSpearPhishingCampaignId =
        state$.value.awarenessCenter.wizardSpearPhishingCampaign.generateEmailsStep
          .createdSpearPhishingCampaignId

      return api.wizardCampaign
        .saveCampaign(state$.value.partnerOptions.selectedCompany?.value, {
          campaignId: campaignData.id || createdSpearPhishingCampaignId || undefined,
          flowType: CAMPAIGN_TYPES_VALUES.SPEAR_PHISHING,
          name: campaignData.name,
          defaultLocaleId: campaignData.defaultLocaleId,
          isTournament: campaignData.isTournament,
          participantsChoices: campaignData.participants.included,
          participantsExcludeChoices: campaignData.participants.excluded,
          timezone: campaignData.timezone,
          scheduleTime: moment(`${campaignData.launchDate} ${campaignData.launchTime}`).format(
            'YYYY-MM-DDTHH:mm'
          ),
          endDate: moment(campaignData.endDate).format('YYYY-MM-DD'),
          closeTime: moment(campaignData.closeTime).format('YYYY-MM-DDTHH:mm[Z]'),
          sendRemindersUntil: campaignData.sendRemindersUntil,
          emailLimit: campaignData.maxEmailPerDay,
          sendAllWeek: campaignData.sendAllWeek,
          landingPageId: campaignData.selectedLandingPage.id,
          trainingIds,
          deliveryType: campaignData.delivery,
          sendReminderEveryXBusinessDays: campaignData.sendReminderEveryXBusinessDays,
          spearPhishingCampaignData: campaignData.spearPhishingCampaignData,
          randomized: campaignData.randomized,
          sendCampaignFailedEmail: campaignData.sendCampaignFailedEmail,
          sendCampaignSummaryToCampaignReportRecipients:
            campaignData.sendCampaignSummaryToCampaignReportRecipients,
          notifications: campaignData.notifications,
        })
        .pipe(
          mergeMap(({ response }) => of(mapSaveSuccessCallbacks[action.type](response, action))),
          catchError((error) => of(epicsErrorHandler(mapSaveErrorCallbacks[action.type], error))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    })
  )

const approveCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_AND_APPROVE_CAMPAIGN_ASYNC.SUCCESS),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSpearPhishingCampaign) return true
      return false
    }),
    mergeMap(() =>
      api.wizardCampaign
        .approveCampaign(state$.value.awarenessCenter.wizardSpearPhishingCampaign.self.campaign.id)
        .pipe(
          mergeMap(({ response }) => of(approveCampaignSuccess(response))),
          catchError((error) => of(epicsErrorHandler(approveCampaignError, error))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  combineEpics(saveDraftEpic, getCampaignEpic, removeDraftEpic, approveCampaignEpic),
  landingPageStepEpics
)
